import { type FC, type ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';

import { Box, Button } from '@packages/shared';
import { ArrowMediumDown32 } from '@packages/themes/icons';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import type { SelectedFilterValue } from '../../types';
import { useNestedFilterDialogs } from './useNestedFilterDialogs';

const FilterSettingsMobilePopovers = dynamic(
  () =>
    import(/* webpackChunkName: 'FilterSettingsMobilePopovers' */ './FilterSettingsMobilePopovers'),
  {
    ssr: false,
  },
);

export const FilterSettingsMobileFragment = graphql(/* GraphQL */ `
  fragment FilterSettingsMobileFragment on SearchProductResult {
    ...FilterSettingsMobilePopoversFragment
    availableSortingOrders {
      key
      displayName
    }
  }
`);

export type FilterSettingsMobileProps = {
  maskedData: FragmentType<typeof FilterSettingsMobileFragment>;
  /** Callback fired when a user submits the selection of one or more filter values */
  onSubmit?: (filterValues: SelectedFilterValue[]) => void;
  /** Renders the content of the categories dialog, don't forget to invoke the closeDialog callback on selection */
  renderCategoryDialogContent: (closeDialog: () => void) => ReactNode;
  /** The list of currently selected filter values */
  selectedFilterValues: SelectedFilterValue[];
  /** Apply a workaround for a graphical glitch with the close button documented in https://github.com/mui/material-ui/issues/12879 */
  applyCloseButtonPaddingWorkaround?: boolean;
};

const messages = defineMessages({
  filterButton: {
    id: 'filters.selection.mobile.filterButton.text',
    defaultMessage: 'Filtern / Sortieren',
  },
  categoryButton: {
    id: 'filters.selection.mobile.categoryButton.text',
    defaultMessage: 'Kategorien',
  },
});

/**
 * A button that allows a user to select filter values on mobile using nested dialogs.
 *
 * @remarks
 * There can be multiple dialogs open at the same time
 * */
export const FilterSettingsMobile: FC<FilterSettingsMobileProps> = ({
  maskedData,
  selectedFilterValues: initiallySelectedFilterValues,
  onSubmit,
  renderCategoryDialogContent,
  applyCloseButtonPaddingWorkaround,
}) => {
  const data = getFragmentData(FilterSettingsMobileFragment, maskedData);

  const { formatMessage } = useIntl();

  const {
    selectedFilterId,
    activeFilterIds,
    selectedFilterValues,
    hasFilterChanges,
    selectFilterValues,
    dialogControls,
  } = useNestedFilterDialogs({
    initiallySelectedFilterValues,
  });

  // ATTENTION: multiple dialogs can be open at the same time! Filter selection works like a 2-step wizard
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        sx={{
          flex: 1,
          height: '1.5rem',
          '& svg': {
            height: '2rem',
            width: '2rem',
          },
        }}
        endIcon={<ArrowMediumDown32 />}
        onClick={dialogControls.categoryDialog.open}
      >
        <Box
          sx={{
            width: '100%',
            marginRight: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
          }}
        >
          {formatMessage(messages.categoryButton)}
        </Box>
      </Button>
      <Button
        sx={{
          flex: 1,
          height: '1.5rem',
          '& svg': {
            height: '2rem',
            width: '2rem',
          },
        }}
        endIcon={<ArrowMediumDown32 />}
        onClick={dialogControls.filterDialog.open}
      >
        <Box
          sx={{
            width: '100%',
            marginRight: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
          }}
        >
          <FormattedMessage {...messages.filterButton} />
        </Box>
      </Button>

      <FilterSettingsMobilePopovers
        maskedData={data}
        selectedFilterId={selectedFilterId}
        activeFilterIds={activeFilterIds}
        selectedFilterValues={selectedFilterValues}
        hasFilterChanges={hasFilterChanges}
        dialogControls={dialogControls}
        renderCategoryDialogContent={renderCategoryDialogContent}
        applyCloseButtonPaddingWorkaround={applyCloseButtonPaddingWorkaround}
        onChange={selectFilterValues}
        onSubmit={onSubmit}
      />
    </Box>
  );
};
